import { TFunction, useTranslation, Trans } from 'react-i18next'
import moment from 'moment'
import React, { useState } from 'react'
import {
	Card,
	CardSubSection,
	AkaButton,
	HeadSection,
	AkaTypography
} from 'components'
import theme from 'components/themeProvider/theme'
import { useFormik } from 'formik'
import { Input, Typography, Form, Tooltip } from 'antd'
import * as Yup from 'yup'
import { ApolloError } from 'apollo-client'
import { StylesDictionary } from 'components/typography/styles'
import { ExecutionResult } from 'graphql'
import { SoftwareSystemType, SealLinkType } from 'generated/types'

moment.locale('de')

const FormItem = Form.Item
const { Paragraph } = Typography

const styles: StylesDictionary = {
	buttonWrapper: {
		display: 'flex',
		justifyContent: 'flex-end',
		paddingBottom: 16
	},
	infoRow: {
		display: 'flex',
		justifyContent: 'start'
	},
	infoItem: {
		display: 'flex',
		flexDirection: 'column',
		minWidth: 200
	},
	topBorder: {
		borderTop: `1px solid ${theme.gray}`
	},
	title: {
		fontWeight: 'bold'
	},
	formItem: {
		maxWidth: 300
	},
	buttonSection: { display: 'flex', justifyContent: 'space-between' }
}

const columns = [
	{
		title: 'UNTERNEHMEN',
		dataIndex: 'manufacturerName',
		key: 'manufacturerName'
	},
	{
		title: 'HZV-SIEGEL',
		dataIndex: 'hzvSeal',
		key: 'hzvSeal'
	}
]

const SealColumn = ({
	sealLinks,
	t,
	systemName
}: {
	sealLinks?: (SealLinkType | null)[] | null
	t: TFunction
	systemName: string
}) => {
	const safeLinks =
		sealLinks?.filter((l): l is SealLinkType => l !== null) ?? []

	if (safeLinks.length === 0)
		return (
			<span>
				<Trans
					i18nKey="account.systemList.seal.noSealMessage"
					values={{ systemName }}
					components={{ b: <b /> }}
				/>
			</span>
		)
	const primarySeal = safeLinks[0]

	const catalogCodes = safeLinks.map(l => l.catalogCode).join(', ')
	const tooltipText = t('account.systemList.seal.certifiedFor', {
		catalogCodes
	})

	const inlineUrl = primarySeal.url
	let downloadUrl = inlineUrl
	try {
		const url = new URL(inlineUrl)
		url.searchParams.set('download', '1')
		downloadUrl = url.toString()
	} catch (e) {
		// Fallback: if URL is invalid, append download param naively
		downloadUrl = inlineUrl.includes('?')
			? `${inlineUrl}&download=1`
			: `${inlineUrl}?download=1`
	}

	return (
		<Tooltip title={tooltipText} placement="right">
			<div
				style={{
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					marginTop: 4
				}}
			>
				<img
					src={inlineUrl}
					alt={t('account.systemList.seal.sealAlt')}
					style={{ width: 200 }}
				/>

				<a
					href={downloadUrl}
					style={{
						marginTop: 4,
						fontSize: 14,
						textAlign: 'center'
					}}
					target="_blank"
					rel="noopener noreferrer"
				>
					{t('account.systemList.seal.download')}
				</a>
			</div>
		</Tooltip>
	)
}

const validationSchema = (t: TFunction) =>
	Yup.object().shape({
		slogan: Yup.string().max(
			255,
			t('account.systemList.editForm.validation.maxLength')
		),
		website: Yup.string()
			.url(t('account.systemList.editForm.validation.website'))
			.max(255, t('account.systemList.editForm.validation.maxLength'))
	})

interface DisplaySystemInfoProps {
	header: { title: string; dataIndex: string; key: string }[]
	data: React.ReactNode[]
}

const DisplaySystemInfo: React.FC<DisplaySystemInfoProps> = ({
	header,
	data
}) => {
	return (
		<div style={styles.infoRow}>
			{data.map((dataPoint: React.ReactNode, i: number) => {
				const isSealColumn = header[i]?.key === 'hzvSeal'
				return (
					<div
						key={i}
						style={{
							...styles.infoItem,
							...(isSealColumn && {
								marginLeft: 40,
								alignItems: 'center',
								textAlign: 'center'
							})
						}}
					>
						<AkaTypography
							style={{
								...styles.title,
								...(isSealColumn && {
									textAlign: 'center',
									width: '100%'
								})
							}}
							variant="body2"
						>
							{i < header.length ? header[i].title : ''}
						</AkaTypography>

						<AkaTypography variant="body2">{dataPoint}</AkaTypography>
					</div>
				)
			})}
		</div>
	)
}

export interface SystemCard {
	item: SoftwareSystemType
	onSubmit: (
		systemId: string,
		slogan: string,
		website: string
	) => Promise<ExecutionResult>
	loading: boolean
	error: ApolloError | undefined
}

export const SystemCard = ({ item, onSubmit, loading, error }: SystemCard) => {
	const [t] = useTranslation()
	const [isEdit, setIsEdit] = useState(false)

	const {
		handleSubmit,
		handleChange,
		handleBlur,
		values,
		errors,
		touched,
		isValid
	} = useFormik({
		initialValues: {
			slogan: item.slogan,
			website: item.website
		},
		onSubmit: ({ slogan, website }) => {
			onSubmit(item.id, slogan, website).then(() => {
				!error && setIsEdit(false)
			})
		},
		validationSchema: validationSchema(t)
	})

	const firstColumn = (
		<div style={{ display: 'flex', flexDirection: 'column' }}>
			<span>{item.manufacturerName}</span>

			<AkaTypography style={{ ...styles.title, marginTop: 16 }} variant="body2">
				{t('account.systemList.labels.website')}
			</AkaTypography>

			{isEdit ? (
				<FormItem
					style={styles.formItem}
					hasFeedback={true}
					validateStatus={touched.website && errors.website ? 'error' : ''}
					help={touched.website && errors.website}
				>
					<Input
						id="website"
						name="website"
						type="text"
						placeholder={t('account.systemList.editForm.placeholders.website')}
						onChange={handleChange}
						value={values.website}
						onBlur={handleBlur}
					/>
				</FormItem>
			) : (
				<a
					href={item.website}
					target="_blank"
					rel="noopener noreferrer"
					style={{ marginTop: 4 }}
				>
					{item.website}
				</a>
			)}
		</div>
	)

	const secondColumn = (
		<SealColumn sealLinks={item.sealLinks} t={t} systemName={item.systemName} />
	)
	const formContent = (
		<>
			<CardSubSection>
				<HeadSection id={item.id} title={item.systemName} tags={[]} />
				{isEdit ? (
					<FormItem
						style={styles.formItem}
						hasFeedback={true}
						validateStatus={touched.slogan && errors.slogan ? 'error' : ''}
						help={touched.slogan && errors.slogan ? errors.slogan : ''}
					>
						<Input
							id="slogan"
							name="slogan"
							type="text"
							placeholder={t('account.systemList.editForm.placeholders.slogan')}
							onChange={handleChange}
							value={values.slogan}
							onBlur={handleBlur}
						/>
					</FormItem>
				) : (
					<AkaTypography
						style={{ marginBottom: 24, height: 32 }}
						variant="body2"
					>
						{item.slogan}
					</AkaTypography>
				)}
			</CardSubSection>

			<CardSubSection gray={true}>
				<DisplaySystemInfo
					header={columns}
					data={[firstColumn, secondColumn]}
				/>
			</CardSubSection>
			<CardSubSection gray={true} style={error && styles.buttonSection}>
				{error && <Paragraph type="danger">{t('core.generalError')}</Paragraph>}
				<div style={styles.buttonWrapper}>
					{!isEdit ? (
						<AkaButton type="primary" onClick={() => setIsEdit(true)}>
							{t('account.systemList.editForm.edit')}
						</AkaButton>
					) : (
						<AkaButton
							key="submit"
							type="primary"
							htmlType="submit"
							disabled={!isValid}
							loading={loading}
							onSubmit={handleSubmit}
						>
							{t('account.systemList.editForm.save')}
						</AkaButton>
					)}
				</div>
			</CardSubSection>
		</>
	)
	return (
		<Card style={styles.topBorder}>
			{isEdit ? (
				<form onSubmit={handleSubmit}>{formContent}</form>
			) : (
				formContent
			)}
		</Card>
	)
}
